/**
 * POS-Style Product Picker Modal
 *
 * Full-screen modal for visually browsing and selecting products.
 * Supports two modes:
 * - All Items: Shows all items including variants (flat list)
 * - Products: Shows only template/standalone items, with variant selector for templates
 *
 * Includes a cart sidebar showing added items with quantities and totals.
 * Reuses existing GalleryGridLayout and ListViewSwitcher components.
 */

import { useState, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ListViewSwitcher } from '@/features/common/components/ListViewSwitcher';
import { GalleryGridLayout } from '@/features/common/components/GalleryGridLayout';
import { useInventoryListWithPagination } from '@/features/inventory/api/inventoryHooks';
import { useDebounce } from '@/lib/hooks/useDebounce';
import type { ListViewLayoutMode } from '@/lib/views/types';
import type { FilterDescriptor } from '@/lib/erp';

import { formatCurrency } from './formatCurrency';
import { BrowseModeToggle } from './BrowseModeToggle';
import { POSTableView } from './POSTableView';
import { POSCartSidebar } from './POSCartSidebar';
import { VariantSelectorModal } from './VariantSelectorModal';
import { CustomizationFormModal } from '@/features/customization/components/CustomizationFormModal';
import { frappeMethod } from '@/lib/erp/utils';
import type { SalesOrderItemCustomization } from '@/lib/erp/types/order';
import type { POSItem, POSCartItem, POSBrowseMode } from './types';

interface POSProductPickerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectItem: (itemCode: string, customizations?: SalesOrderItemCustomization[]) => void;
  onRemoveItem?: (itemCode: string, lineId?: string) => void;
  selectedItemCodes?: string[];
  cartItems?: POSCartItem[];
  currency?: string;
  company?: string;
  /** When true, only show items with chat_checkout_enabled=1 */
  chatMode?: boolean;
  /** Called when a Product Bundle item is clicked (parent handles bundle configurator) */
  onBundleItemClick?: (itemCode: string, itemName: string) => void;
}

export function POSProductPickerModal({
  open,
  onOpenChange,
  onSelectItem,
  onRemoveItem,
  selectedItemCodes = [],
  cartItems = [],
  currency = 'THB',
  company,
  chatMode = false,
  onBundleItemClick,
}: POSProductPickerModalProps) {
  const [viewMode, setViewMode] = useState<ListViewLayoutMode>('gallery');
  const [browseMode, setBrowseMode] = useState<POSBrowseMode>('products');
  const [searchQuery, setSearchQuery] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const pageSize = 20;

  // Variant selector state
  const [variantSelectorOpen, setVariantSelectorOpen] = useState(false);
  const [selectedTemplateCode, setSelectedTemplateCode] = useState<string>('');

  // Personalization state — defers adding to cart until customization is complete
  const [pendingCustomizationCode, setPendingCustomizationCode] = useState<string | null>(null);
  const [pendingCustomizationName, setPendingCustomizationName] = useState<string | undefined>();

  const debouncedSearch = useDebounce(searchQuery, 300);
  const showCart = cartItems.length > 0;

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen) {
        setSearchQuery('');
        setPageIndex(0);
      }
      onOpenChange(newOpen);
    },
    [onOpenChange]
  );

  // Build filters based on browse mode
  const filterDescriptors = useMemo((): FilterDescriptor[] => {
    const filters: FilterDescriptor[] = [
      { field: 'disabled', operator: 'equals', value: 0 },
    ];

    if (chatMode) {
      filters.push({ field: 'chat_checkout_enabled', operator: 'equals', value: 1 });
    }

    if (browseMode === 'products') {
      // Only show template items and standalone items (not variants)
      // variant_of is NULL or empty for templates and standalone items
      // Use "isNotSet" operator which maps to "is not set" in Frappe
      filters.push({ field: 'variant_of', operator: 'isNotSet', value: null });
    }

    return filters;
  }, [browseMode, chatMode]);

  const { data, isLoading } = useInventoryListWithPagination({
    searchQuery: debouncedSearch,
    pageIndex,
    pageSize,
    filterDescriptors,
    fields: [
      'name',
      'item_code',
      'item_name',
      'image',
      'variant_of',
      'has_variants',
      'primary_image',
      'standard_rate',
      'item_group',
      'customization_template',
    ],
  });

  const items = (data?.data ?? []) as POSItem[];
  const totalCount = data?.total ?? 0;

  const getRowId = useCallback((item: POSItem) => item.item_code || item.name || '', []);

  const getImagePath = useCallback((item: POSItem) => {
    return item.primary_image || item.image || null;
  }, []);

  const getTitleText = useCallback((item: POSItem) => item.item_name || item.item_code || '', []);

  const getSubtitleText = useCallback(
    (item: POSItem) => {
      if (browseMode === 'products' && item.has_variants) {
        return 'Multiple options available';
      }
      const price = item.standard_rate;
      if (price && Number(price) > 0) {
        return formatCurrency(Number(price), currency);
      }
      return item.item_code || '';
    },
    [browseMode, currency]
  );

  const getBadgeText = useCallback((item: POSItem) => {
    if (item.customization_template) return 'Personalize';
    return undefined;
  }, []);

  // Route item through personalization if it has a customization template
  const addItemWithPersonalization = useCallback(
    (itemCode: string, itemName?: string, hasCustomization?: boolean) => {
      if (hasCustomization) {
        // Show personalization modal (auto-skips via API if no template found)
        setPendingCustomizationCode(itemCode);
        setPendingCustomizationName(itemName);
      } else {
        // No customization — add directly without modal
        onSelectItem(itemCode);
      }
    },
    [onSelectItem]
  );

  const handleItemClick = useCallback(
    async (item: POSItem) => {
      const itemCode = item.item_code || '';
      if (!itemCode) return;

      // In products mode, if item has variants, open variant selector
      if (browseMode === 'products' && item.has_variants) {
        setSelectedTemplateCode(itemCode);
        setVariantSelectorOpen(true);
        return;
      }

      // Check if this item is a Product Bundle (only if parent handles bundles)
      // In chatMode, allow re-adding items (cart increments qty)
      if (onBundleItemClick && (chatMode || !selectedItemCodes.includes(itemCode))) {
        try {
          const exists = await frappeMethod<{ exists: boolean }>(
            'zaviago_backend.storefront.chat_order.check_is_bundle',
            { item_code: itemCode }
          );
          if (exists?.exists) {
            onBundleItemClick(itemCode, item.item_name || itemCode);
            return;
          }
        } catch {
          // If check fails, fall through to normal add
        }
      }

      // Otherwise, route through personalization check
      if (chatMode || !selectedItemCodes.includes(itemCode)) {
        addItemWithPersonalization(
          itemCode,
          item.item_name || item.item_code,
          !!item.customization_template
        );
      }
    },
    [browseMode, chatMode, selectedItemCodes, addItemWithPersonalization, onBundleItemClick]
  );

  const handleVariantSelect = useCallback(
    (itemCode: string) => {
      if (chatMode || !selectedItemCodes.includes(itemCode)) {
        // Variants may inherit customization from template — let the modal auto-detect
        addItemWithPersonalization(itemCode, undefined, true);
      }
    },
    [chatMode, selectedItemCodes, addItemWithPersonalization]
  );

  const handleClose = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const handleBrowseModeChange = useCallback((mode: POSBrowseMode) => {
    setBrowseMode(mode);
    setPageIndex(0);
  }, []);

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="!max-w-none w-[95vw] h-[90vh] flex flex-col p-0">
          {/* Header */}
          <div className="px-6 pr-14 py-4 border-b flex items-center gap-4 flex-shrink-0">
            <DialogTitle className="text-lg font-semibold flex-shrink-0">Add Products</DialogTitle>

            {/* Search - centered */}
            <div className="flex-1 max-w-md mx-auto">
              <Input
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPageIndex(0);
                }}
              />
            </div>

            {/* Browse Mode Toggle - left of view switcher */}
            <BrowseModeToggle mode={browseMode} onChange={handleBrowseModeChange} />

            <ListViewSwitcher
              currentMode={viewMode}
              onModeChange={setViewMode}
              disabled={isLoading}
            />
          </div>

          {/* Main content area */}
          <div className="flex-1 flex overflow-hidden">
            {/* Product grid/list */}
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="flex-1 overflow-auto p-6">
                {isLoading ? (
                  <div className="py-12 text-center text-muted-foreground">
                    Loading products...
                  </div>
                ) : items.length === 0 ? (
                  <div className="py-12 text-center text-muted-foreground">No products found</div>
                ) : viewMode === 'gallery' ? (
                  <GalleryGridLayout
                    items={items}
                    getRowId={getRowId}
                    getImagePath={getImagePath}
                    getTitleText={getTitleText}
                    getSubtitleText={getSubtitleText}
                    getBadgeText={getBadgeText}
                    onRowClick={handleItemClick}
                    isLoading={false}
                    hasImageFields={true}
                    sizePx={180}
                  />
                ) : (
                  <POSTableView
                    items={items}
                    selectedItemCodes={selectedItemCodes}
                    onSelectItem={handleItemClick}
                    currency={currency}
                  />
                )}
              </div>

              {/* Pagination */}
              <div className="px-6 py-4 border-t flex items-center justify-between text-sm flex-shrink-0">
                <span className="text-muted-foreground">{totalCount} products</span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pageIndex === 0}
                    onClick={() => setPageIndex((p) => p - 1)}
                  >
                    Previous
                  </Button>
                  <span className="text-muted-foreground">Page {pageIndex + 1}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={items.length < pageSize}
                    onClick={() => setPageIndex((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>

            {/* Cart Sidebar */}
            {showCart && (
              <POSCartSidebar
                cartItems={cartItems}
                currency={currency}
                onRemoveItem={onRemoveItem}
                onClose={handleClose}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Variant Selector Modal */}
      <VariantSelectorModal
        open={variantSelectorOpen}
        onOpenChange={setVariantSelectorOpen}
        templateItemCode={selectedTemplateCode}
        onSelectVariant={handleVariantSelect}
        currency={currency}
      />

      {/* Personalization Modal — auto-skips if item has no customization template */}
      <CustomizationFormModal
        itemCode={pendingCustomizationCode}
        itemName={pendingCustomizationName}
        company={company}
        open={!!pendingCustomizationCode}
        onSubmit={(customizations) => {
          if (pendingCustomizationCode) {
            onSelectItem(pendingCustomizationCode, customizations);
          }
          setPendingCustomizationCode(null);
          setPendingCustomizationName(undefined);
        }}
        onSkip={() => {
          if (pendingCustomizationCode) {
            onSelectItem(pendingCustomizationCode);
          }
          setPendingCustomizationCode(null);
          setPendingCustomizationName(undefined);
        }}
      />
    </>
  );
}
